import { useMutation, useQuery } from "@tanstack/react-query"
import {
  CustomUseMutationOptions,
  THREE_MINUTES,
} from "@/shared/lib/react-query/react-query.lib"
import {
  getApiSubscriptionList,
  getUsageInfo,
  grantInternalApiSubscription,
  issueApiSubscriptionKey,
  makeApiSubscription,
  undoApiUnsubscribe,
  unsubscribeApiSubscription,
} from "./subscription.api"
import { SubscriptionQueryKeysEnum } from "./subscription.constants"
import { ApiSubscription } from "./subscription.types"

export const useUsageInfoQuery = ({
  enabled,
  refetchOnWindowFocus = true,
}: {
  enabled?: boolean
  refetchOnWindowFocus?: boolean
}) => {
  return useQuery<string>({
    queryKey: [SubscriptionQueryKeysEnum.FetchUsage],
    queryFn: getUsageInfo,
    enabled,
    staleTime: THREE_MINUTES,
    refetchOnWindowFocus,
  })
}

// api subscription
export const useGetApiSubscriptionListQuery = () => {
  return useQuery<ApiSubscription[]>({
    queryKey: [SubscriptionQueryKeysEnum.ApiSubscription],
    queryFn: getApiSubscriptionList,
  })
}

export const useMakeApiSubscriptionMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: makeApiSubscription,
    onSuccess,
    onError,
    onSettled,
  })
}

export const useGrantInternalApiSubscriptionMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: grantInternalApiSubscription,
    onSuccess,
    onError,
    onSettled,
  })
}

export const useUnsubscribeApiSubscriptionMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: unsubscribeApiSubscription,
    onSuccess,
    onError,
    onSettled,
  })
}

export const useUndoApiSubscribeMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: undoApiUnsubscribe,
    onSuccess,
    onError,
    onSettled,
  })
}

// What mutate() was given.
export type IssueApiSubscriptionKeyVariables = Parameters<
  typeof issueApiSubscriptionKey
>[0]

// Typed here, not with CustomUseMutationOptions, so the callbacks get the request
// they answer. useMutation hands a pending mutation the options of the caller's
// latest render, so anything a callback reads from that render, such as which
// subscription the view shows, may be newer than the request.
export const useIssueApiSubscriptionKeyMutation = ({
  onSuccess,
  onError,
  onSettled,
}: {
  onSuccess?: (
    apiKey: string,
    variables: IssueApiSubscriptionKeyVariables
  ) => void
  // ApiKeyAlreadyIssuedError, or what handleAxiosError threw.
  onError?: (error: Error, variables: IssueApiSubscriptionKeyVariables) => void
  onSettled?: () => void
}) => {
  return useMutation({
    mutationFn: issueApiSubscriptionKey,
    onSuccess,
    onError,
    onSettled,
  })
}
